"use strict";

import { setStoredProjectFile } from "../utils/storage";
import {
  Button,
  FormControl,
  InputLabel,
  Grid,
  IconButton,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tabs,
  Tab,
  Container,
  Box,
  Chip,
  Paper,
  Stack,
  Typography,
  FormHelperText,
} from "@mui/material";
import axios from "axios";
import React, { useContext, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import classes from "../Assets/Styles/Global.module.css";
import Loader from "../Components/Loader.js";
import {
  GET_USERS,
  UPDATE_PROJECT,
  UPDATE_DIALOGUE_USED_MODEL,
  CREATE_ALL_VOCALS_AUTOMATICALLY,
  TRANSLATE,
} from "../ApiEndpoints.js";
import { getProjectById } from "../features/projects/api.js";
import { postWithFirebaseAuth } from "../utils/firebaseApiClient.js";
import { languages } from "../Data/languages.js";
import DeleteIcon from "@mui/icons-material/Delete";
import AudiotrackIcon from "@mui/icons-material/Audiotrack";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import {
  getLanguageName,
  getRoleOptionLabel,
  getTaskName,
} from "../utils/generalFunctions.js";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
// import { ConfirmToast } from "react-confirm-toast";
import { toast } from "react-toastify";
import UserSelectAutocomplete from "../Components/UserSelectAutoComplete/index.js";
import ProjectSubtitleUpload from "../Components/ProjectSubtitleUpload/index.js";
import ProjectSubtitleFiles from "../Components/ProjectSubtitleUpload/ProjectSubtitleFiles.js";
import ImportCharactersPanel from "../Components/ImportCharactersPanel.js";
import { Store } from "../Store";
import { useProjectAudioPlayback } from "../hooks/useProjectAudioPlayback.js";
import {
  DEFAULT_VOICE_ENGINE,
  VOICE_ENGINE_OPTIONS,
  getSupportedVoiceEngine,
  isSupportedVoiceEngine,
} from "../Data/voiceEngines.js";
import ModeCards from "../features/adminActions/ModeCards.js";
import {
  CREATE_TAKES_MODE_OPTIONS,
  RETRANSLATE_MODE_OPTIONS,
} from "../features/adminActions/modeOptions.js";
import ReloadVideoButton from "../features/adminActions/ReloadVideoButton.js";
import { reloadVideoBlocker } from "../features/adminActions/taskPrerequisites.js";
import { getProjectTaskStatuses } from "../features/adminActions/taskStatuses.js";
import {
  describeVideoIngestion,
  getVideoIngestionState,
  pendingVideoIngestion,
} from "../features/adminActions/videoIngestion.js";

// Matches the audio types offered when the stems were uploaded.
const AUDIO_TYPE_OPTIONS = [
  { value: "dialogue", label: "Dialogue" },
  { value: "music", label: "Music" },
  { value: "effect", label: "Effects" },
  { value: "musicEffects", label: "Music & Effects" },
  { value: "dialogueMusicEffect", label: "Dialogue, Music, Effects" },
];

const AudioAssetCard = ({
  title,
  reference,
  projectId,
  details = [],
  action = null,
}) => {
  const { playbackUrl, playbackUnavailable } = useProjectAudioPlayback({
    reference,
    projectId,
  });

  return (
    <Paper
      variant="outlined"
      sx={{
        width: "100%",
        minWidth: 0,
        boxSizing: "border-box",
        overflow: "hidden",
        p: { xs: 1.5, sm: 2.5 },
        borderColor: "#dfe4f2",
        borderRadius: 2,
        boxShadow: "0 4px 14px rgba(50, 70, 120, 0.06)",
      }}
    >
      <Stack spacing={2}>
        <Stack direction="row" spacing={1.25} alignItems="center">
          <Box
            sx={{
              display: "grid",
              placeItems: "center",
              width: 38,
              height: 38,
              flexShrink: 0,
              color: "#5570dc",
              bgcolor: "#eef1ff",
              borderRadius: 1.5,
            }}
          >
            <AudiotrackIcon fontSize="small" />
          </Box>
          <Box sx={{ minWidth: 0, flexGrow: 1 }}>
            <Typography variant="subtitle1" fontWeight={600} lineHeight={1.25}>
              {title}
            </Typography>
            {details.length > 0 && (
              <Stack direction="row" gap={0.75} mt={0.75} flexWrap="wrap">
                {details.map((detail) => (
                  <Chip
                    key={detail}
                    label={detail}
                    size="small"
                    sx={{ height: 22, bgcolor: "#f4f6fb" }}
                  />
                ))}
              </Stack>
            )}
          </Box>
          {action}
        </Stack>

        {playbackUrl ? (
          <>
            <Box
              component="audio"
              controls
              preload="metadata"
              src={playbackUrl}
              aria-label={`${title} audio player`}
              sx={{ display: "block", width: "100%", maxWidth: "100%" }}
            >
              Your browser does not support the audio element.
            </Box>
            <Button
              component="a"
              href={playbackUrl}
              target="_blank"
              rel="noopener noreferrer"
              size="small"
              endIcon={<OpenInNewIcon fontSize="small" />}
              sx={{ alignSelf: "flex-start", px: 0 }}
            >
              Open audio
            </Button>
          </>
        ) : reference ? (
          <Typography variant="body2" color="text.secondary">
            {playbackUnavailable ? "Audio is unavailable" : "Loading audio…"}
          </Typography>
        ) : (
          <Typography variant="body2" color="text.secondary">
            No audio file available
          </Typography>
        )}
      </Stack>
    </Paper>
  );
};

const ProjectEditScreen = () => {
  const params = useParams();
  const { id: projectId } = params;
  const navigate = useNavigate();
  const { state, dispatch: ctxDispatch } = useContext(Store);
  const { userInfo } = state;
  const [videoFile, setVideoFile] = useState("");
  // The loaded project document: task statuses and dialogue count decide
  // whether the video can be reloaded.
  const [projectRecord, setProjectRecord] = useState(null);
  const [dialogueAudioFile, setDialogueAudioFile] = useState("");
  // const [dialogueAudioURL, setDialogueAudioURL] = useState();
  const [fxFile, setFxFile] = useState("");
  const [projectName, setProjectName] = useState("");
  // Detected by transcription; "" until then.
  const [projectOriginalLang, setProjectOriginalLang] = useState("");
  const [projectFinalLang, setProjectFinalLang] = useState("Final Languages");
  const [projectFinalLangs, setProjectFinalLangs] = useState([]);
  const [uploadedAudioTypes, setUploadedAudioTypes] = useState([]);
  const [originalSubtitleObjectName, setOriginalSubtitleObjectName] =
    useState("");
  const [translatedSubtitles, setTranslatedSubtitles] = useState({});
  const [defaultVoiceModel, setDefaultVoiceModel] = useState("");
  const [defaultVoiceLanguage, setDefaultVoiceLanguage] = useState("");
  const [isSettingDefaultVoiceModel, setIsSettingDefaultVoiceModel] =
    useState(false);
  const [createTakesMode, setCreateTakesMode] = useState("soft");
  const [createTakesLanguage, setCreateTakesLanguage] = useState("");
  const [translateMode, setTranslateMode] = useState("soft");
  const [translateLanguage, setTranslateLanguage] = useState("");

  // Removed unused state such as videoURL, employees and selectEmployee after
  // adopting the UserSelectAutocomplete component, which manages its own data.

  const [selectRole, setSelectRole] = useState("Select role");
  const [displaySelection, setDisplaySelection] = useState([]);
  const [upLoader, setUpLoader] = useState(false);
  const [selectedUserObj, setSelectedUserObj] = useState(null);
  const [activeTab, setActiveTab] = useState("configuration");
  const [initialSnapshot, setInitialSnapshot] = useState("");
  const savingRef = React.useRef(false);
  const availableDefaultLanguages = React.useMemo(() => {
    // Ensure unique language codes for the default selector.
    return Array.from(new Set(projectFinalLangs || []));
  }, [projectFinalLangs]);
  /**
   * Serializes a snapshot of project data for change detection.
   * @param {object} snapshot - Current project state to serialize.
   * @returns {string} JSON string representation of the snapshot.
   */
  const serializeSnapshot = React.useCallback((snapshot) => {
    // Convert the snapshot object into a string for comparisons.
    return JSON.stringify(snapshot);
  }, []);

  const ALL_ROLES = [
    "transcriber",
    "translator",
    "performancedirection",
    "lipqualityassurance",
    "qualityassurance",
    "performancedirectionversions",
  ];

  /**
   * Ensures role input is treated as an array to simplify downstream handling.
   * @param {string|string[]} role - Role code(s) to normalize.
   * @returns {string[]} Normalized list of role codes.
   */
  const normalizeRoles = (role) => {
    // Normalize a single role into a list for consistent processing.
    return Array.isArray(role) ? role : [role];
  };

  /**
   * Formats role codes into a human-readable string for table display.
   * @param {string|string[]} role - Role code(s) to format.
   * @returns {string} Comma-separated list of role labels.
   */
  const formatRoleLabel = (role) => {
    // De-duplicate roles so we do not repeat labels in the UI.
    const uniqueRoles = Array.from(new Set(normalizeRoles(role)));
    // Convert role codes to display names for table presentation.
    return uniqueRoles.map((roleCode) => getTaskName(roleCode)).join(", ");
  };

  /**
   * Submits the default voice model selection for a project language.
   */
  const handleSetDefaultVoiceModel = async () => {
    // Confirm that a model is selected before submitting.
    if (!defaultVoiceModel) {
      toast.error("Please select a default voice model.");
      return;
    }

    // Confirm that a language is selected before submitting.
    if (!defaultVoiceLanguage) {
      toast.error("Please select a language.");
      return;
    }

    try {
      // Lock the UI while the request is in flight.
      setIsSettingDefaultVoiceModel(true);
      // Fall back from legacy hydrated values before submitting.
      const supportedDefaultVoiceModel =
        getSupportedVoiceEngine(defaultVoiceModel);
      // Submit the project-level default selection to the API.
      await axios.post(UPDATE_DIALOGUE_USED_MODEL, {
        projectId,
        languageCode: defaultVoiceLanguage,
        usedModel: supportedDefaultVoiceModel,
      });
      // Keep the selector aligned if a legacy value was remapped.
      setDefaultVoiceModel(supportedDefaultVoiceModel);
      // Confirm success to the user.
      toast.success("Default voice model updated.");
    } catch (error) {
      // Log and surface any request failures.
      console.error("Failed to set default voice model:", error);
      toast.error("Failed to set default voice model.");
    } finally {
      // Re-enable the UI after the request completes.
      setIsSettingDefaultVoiceModel(false);
    }
  };

  /**
   * Requests vocal take generation for a selected language and mode.
   */
  const handleCreateTakes = async () => {
    // Ensure a language has been selected before making a request.
    if (!createTakesLanguage) {
      toast.error("Please select a language.");
      return;
    }

    // Based on the Create/Regenerate takes flow from the project admin actions page.
    // We are adopting the "takes" naming convention for this workflow going forward.
    const runGenerate = async () => {
      try {
        // TODO: Confirm the target language already has translations for every dialogue line.
        // Submit the generation request to the backend.
        await postWithFirebaseAuth(CREATE_ALL_VOCALS_AUTOMATICALLY, {
          projectId,
          language: createTakesLanguage,
          soft: createTakesMode === "soft",
        });
        // Confirm the request has been sent.
        toast.success(
          `Takes have been requested for ${getLanguageName(
            createTakesLanguage
          )}`
        );
      } catch (error) {
        // Surface errors to the user when generation fails, including an
        // unfinished prerequisite reported by the backend (409).
        toast.error(
          error?.response?.data?.error ||
            "An error has occurred while creating translated takes"
        );
      }
    };

    // Require confirmation for hard regenerations.
    if (createTakesMode === "hard") {
      const toastId = toast.info(
        ({ closeToast }) => (
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            <span>
              Overwrite all will replace existing takes. Continue?
            </span>
            <div
              style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}
            >
              <Button
                variant="contained"
                size="small"
                onClick={async () => {
                  toast.dismiss(toastId);
                  await runGenerate();
                }}
              >
                Confirm
              </Button>
              <Button
                variant="outlined"
                size="small"
                onClick={() => toast.dismiss(toastId)}
              >
                Cancel
              </Button>
            </div>
          </div>
        ),
        { autoClose: false, closeOnClick: false }
      );
    } else {
      // Soft mode can run immediately without confirmation.
      await runGenerate();
    }
  };

  /**
   * Requests translation creation or regeneration for the selected language.
   */
  const handleCreateTranslation = async () => {
    // Ensure a language has been selected before making a request.
    if (!translateLanguage) {
      toast.error("Please select a language.");
      return;
    }

    // Based on the Create/Regenerate translation flow from the project admin actions page.
    const runTranslate = async () => {
      try {
        // TODO: Confirm transcriptions are ready for all imported audio before translation.
        await axios.post(TRANSLATE, {
          projectId,
          newLanguage: translateLanguage,
          mode: translateMode,
        });
        toast.success(
          `Translations have been requested for ${getLanguageName(
            translateLanguage
          )}`
        );
      } catch (error) {
        console.error("Failed to request translations:", error);
        toast.error(
          error?.response?.data?.error || "An error occurred while creating translations"
        );
      }
    };

    // Require confirmation for hard regenerations.
    if (translateMode === "hard") {
      const toastId = toast.info(
        ({ closeToast }) => (
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            <span>
              Overwrite all will replace existing translations. Continue?
            </span>
            <div
              style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}
            >
              <Button
                variant="contained"
                size="small"
                onClick={async () => {
                  toast.dismiss(toastId);
                  await runTranslate();
                }}
              >
                Confirm
              </Button>
              <Button
                variant="outlined"
                size="small"
                onClick={() => toast.dismiss(toastId)}
              >
                Cancel
              </Button>
            </div>
          </div>
        ),
        { autoClose: false, closeOnClick: false }
      );
    } else {
      // Soft mode can run immediately without confirmation.
      await runTranslate();
    }
  };

  useEffect(() => {
    // Redirect non-admins away from the manage screen.
    if (!userInfo || !userInfo.isAdmin) {
      navigate("/");
    }
  }, [userInfo, navigate]);

  useEffect(() => {
    // Normalize legacy hydrated voice model values before submit actions.
    if (
      defaultVoiceModel &&
      !isSupportedVoiceEngine(defaultVoiceModel)
    ) {
      setDefaultVoiceModel(DEFAULT_VOICE_ENGINE);
    }
  }, [defaultVoiceModel]);

  useEffect(() => {
    // Clear the selected language if it was removed from the project.
    if (
      defaultVoiceLanguage &&
      !availableDefaultLanguages.includes(defaultVoiceLanguage)
    ) {
      setDefaultVoiceLanguage("");
    }
  }, [availableDefaultLanguages, defaultVoiceLanguage]);

  const currentSnapshot = React.useMemo(() => {
    // Collect the state needed to detect unsaved changes.
    return {
      projectName,
      projectOriginalLang,
      projectFinalLangs,
      displaySelection,
      videoFile,
      dialogueAudioFile,
      fxFile,
      uploadedAudioTypes,
    };
  }, [
    projectName,
    projectOriginalLang,
    projectFinalLangs,
    displaySelection,
    videoFile,
    dialogueAudioFile,
    fxFile,
    uploadedAudioTypes,
  ]);

  const isDirty = React.useMemo(() => {
    // Skip dirty checks until the initial snapshot is set.
    if (!initialSnapshot) return false;
    // Compare the current state to the stored snapshot.
    return serializeSnapshot(currentSnapshot) !== initialSnapshot;
  }, [currentSnapshot, initialSnapshot, serializeSnapshot]);

  /**
   * Prompts the user when attempting to leave with unsaved changes and
   * keeps the active tab on the configuration panel if they cancel.
   * @returns {boolean} Whether navigation away from the page is allowed.
   */
  const promptUnsavedChanges = React.useCallback(() => {
    // Ask the user to confirm leaving the page.
    const confirmed = window.confirm(
      "You have unsaved changes. Are you sure you want to leave this page?"
    );

    // Keep the user on the configuration tab if they cancel.
    if (!confirmed) {
      setActiveTab("configuration");
    }

    // Return the user's decision for navigation handlers.
    return confirmed;
  }, [setActiveTab]);

  useEffect(() => {
    let isCancelled = false;
    setInitialSnapshot("");

    const fetchData = async () => {
      try {
        const response = await getProjectById(projectId);
        if (isCancelled) return;

        const projectData = {
          ...response.data,
          id: response.data?.id || projectId,
        };

        const initialData = {
          projectName: projectData.name || "",
          projectOriginalLang: projectData.orginalLang || "",
          projectFinalLangs: projectData.finalLang || [],
          displaySelection: projectData.employes || [],
          videoFile: projectData.videoUrl || "",
          dialogueAudioFile: projectData.dialogueAudioUrl || "",
          fxFile: projectData.fxUrl || "",
          uploadedAudioTypes: projectData.uploadedAudioTypes || [],
        };

        // Populate state from the response payload.
        setProjectName(initialData.projectName);
        setProjectOriginalLang(initialData.projectOriginalLang);
        setProjectFinalLangs(initialData.projectFinalLangs);
        setDisplaySelection(initialData.displaySelection);
        setVideoFile(initialData.videoFile);
        setDialogueAudioFile(initialData.dialogueAudioFile);
        setFxFile(initialData.fxFile);
        setUploadedAudioTypes(initialData.uploadedAudioTypes);
        setOriginalSubtitleObjectName(projectData.originalSubtitleObjectName || "");
        setTranslatedSubtitles(projectData.translatedSubtitles || {});
        setProjectRecord(projectData);
        ctxDispatch({ type: "PROJECT_FILE", payload: projectData });
        setStoredProjectFile(projectData);
        setInitialSnapshot(serializeSnapshot(initialData));
      } catch (err) {
        // Log request failures for debugging.
        console.log(err);
      }
    };
    // Trigger the project data load on mount and when the ID changes.
    fetchData();

    return () => {
      isCancelled = true;
    };
  }, [projectId, ctxDispatch, serializeSnapshot]);

  // Previously this component fetched the employee list on mount, but
  // UserSelectAutocomplete now retrieves its own data so that effect was removed.

  /**
   * Persists project updates and resets the dirty state after a successful save.
   * @param {React.FormEvent} e - Submit event from the configuration form.
   */
  const handleUpdateProject = async (e) => {
    // Prevent native form submission and show the loader.
    setUpLoader(true);
    e?.preventDefault();
    savingRef.current = true;

    try {
      // Send the updated project payload to the API.
      await axios.post(UPDATE_PROJECT, {
        name: projectName,
        employes: displaySelection,
        // Omitted when empty so saving never clears the detected language.
        ...(projectOriginalLang ? { orginalLang: projectOriginalLang } : {}),
        finalLang: projectFinalLangs,
        videoUrl: videoFile,
        dialogueAudioUrl: dialogueAudioFile,
        uploadedAudioTypes: uploadedAudioTypes,
        fxUrl: fxFile,
        id: projectId,
      });
      // Reset the dirty tracking snapshot on success.
      setInitialSnapshot(serializeSnapshot(currentSnapshot));
      // Notify the user that the save succeeded.
      toast.success("Saved");
    } catch (error) {
      // Log and surface save failures.
      console.log(error);
      toast.error("Failed to save");
    } finally {
      savingRef.current = false;
      // Hide the loader regardless of request outcome.
      setUpLoader(false);
    }
  };

  /**
   * Removes all entries for a given employee name from the selection list.
   * @param {string} name - Employee name to remove.
   */
  const handleRemoveEmployees = (name) => {
    // Filter out the employee matching the provided name.
    const newItems = displaySelection.filter((item) => item.name !== name);
    // Persist the filtered list to state.
    setDisplaySelection(newItems);
  };

  /**
   * Removes a specific language code from the list of final languages.
   * @param {string} code - Language code to remove.
   */
  const handleRemoveLanguage = (code) => {
    // Remove the specified language from the selection.
    const newItems = projectFinalLangs.filter(
      (languageCode) => languageCode !== code
    );
    // Persist the updated list to state.
    setProjectFinalLangs(newItems);
  };

  useEffect(() => {
    // Skip interception when there are no unsaved changes.
    if (!isDirty) return;

    const confirmLeave = () => {
      // Only confirm if a save isn't already in progress.
      if (!savingRef.current && isDirty) {
        return promptUnsavedChanges();
      }

      // Allow navigation by default.
      return true;
    };

    // Intercept programmatic navigations (push/replace)
    const originalPush = window.history.pushState;
    const originalReplace = window.history.replaceState;

    const wrap = (fn) =>
      function wrapped(state, title, url) {
        // Block navigation if the user declines the confirmation prompt.
        if (!confirmLeave()) return;
        // Continue with navigation when confirmed.
        return fn.apply(window.history, arguments);
      };

    window.history.pushState = wrap(originalPush);
    window.history.replaceState = wrap(originalReplace);

    // Intercept back/forward buttons
    const onPopState = () => {
      // Prevent back/forward navigation when the user cancels the prompt.
      if (!confirmLeave()) {
        // undo the back/forward
        window.history.go(1);
      }
    };
    window.addEventListener("popstate", onPopState);

    return () => {
      // Restore the original history methods and listeners.
      window.history.pushState = originalPush;
      window.history.replaceState = originalReplace;
      window.removeEventListener("popstate", onPopState);
    };
  }, [isDirty, promptUnsavedChanges]);

  useEffect(() => {
    // Only register the unload handler when there are unsaved changes.
    if (!isDirty) return;
    const handleBeforeUnload = (e) => {
      // Trigger the browser confirmation prompt.
      e.preventDefault();
      e.returnValue = ""; // required for Chrome
    };
    // Register the unload listener.
    window.addEventListener("beforeunload", handleBeforeUnload);
    // Clean up the listener on unmount or when changes are saved.
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [isDirty]);

  const actionButtonSx = {
    fontWeight: 600,
    "&.Mui-disabled": {
      color: "rgba(255, 255, 255, 0.7)",
      backgroundColor: "rgba(31, 55, 105, 0.5)",
    },
  };
  const actionSectionSx = {
    border: "1px solid #e4e7ef",
    borderRadius: "var(--radius-md)",
    p: 2,
    backgroundColor: "#fafbfc",
  };

  return (
    <Container
      maxWidth="xl"
      style={{ fontFamily: "var(--font-body)" }}
      sx={{ py: 4 }}
    >
      <Loader Load={upLoader} />
      <ToastContainer position="top-center" />

      <Box
        sx={{
          maxWidth: 1280,
          mx: "auto",
          display: "flex",
          flexDirection: "column",
          gap: 3,
          px: { xs: 1, sm: 2 },
        }}
      >
        <Grid item className={classes["header"]}>
          Manage
        </Grid>

        <Box>
          <Tabs
            value={activeTab}
            onChange={(event, newValue) => setActiveTab(newValue)}
            sx={{
              mb: "-1px",
              minHeight: 48,
              px: { xs: 0, sm: 1 },
              "& .MuiTabs-flexContainer": { columnGap: 2 },
            }}
          >
            <Tab label="Configuration" value="configuration" />
            <Tab label="Assets" value="assets" />
            <Tab label="Actions" value="actions" />
          </Tabs>

          <Box
            sx={{
              border: "1px solid #dfe3eb",
              borderRadius: "var(--radius-md)",
              backgroundColor: "white",
              p: { xs: 1.5, sm: 3 },
              mt: 0,
            }}
          >
            {activeTab === "configuration" && (
              <form onSubmit={handleUpdateProject}>
                <Grid container spacing={3}>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Project Title"
                      type="text"
                      value={projectName}
                      // onChange={(e) => setProjectName(e.target.value)}
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth>
                      <Select
                        value={projectOriginalLang}
                        displayEmpty
                        onChange={(e) => {
                          // Correct the language transcription detected.
                          setProjectOriginalLang(e.target.value);
                        }}
                      >
                        <MenuItem value="" disabled={true}>
                          Original language: not detected yet
                        </MenuItem>
                        {languages.map((language) => (
                          <MenuItem key={language.code} value={language.code}>
                            {language.name}
                          </MenuItem>
                        ))}
                      </Select>
                      <FormHelperText>
                        Detected during transcription. Change it only to correct the detection.
                      </FormHelperText>
                    </FormControl>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Grid container spacing={2} alignItems="center">
                      <Grid item xs={12} sm={8}>
                        <FormControl fullWidth>
                          <Select
                            value={projectFinalLang}
                            onChange={(e) => {
                              // Persist the selected final language candidate.
                              setProjectFinalLang(e.target.value);
                            }}
                          >
                            <MenuItem value="Final Languages" disabled={true}>
                              Final Languages
                            </MenuItem>
                            {languages.map((language) => (
                              <MenuItem
                                key={language.code}
                                value={language.code}
                              >
                                {language.name}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <Button
                          variant="contained"
                          color="primary"
                          fullWidth
                          onClick={() => {
                            // Skip if no language is selected.
                            if (projectFinalLang === "") return;
                            // Add the selected language to the list.
                            setProjectFinalLangs([
                              ...projectFinalLangs,
                              projectFinalLang,
                            ]);
                            // Reset the dropdown after adding.
                            setProjectFinalLang("");
                          }}
                          size="small"
                        >
                          Add
                        </Button>
                      </Grid>
                      <Grid item xs={12}>
                        <Grid container gap={1}>
                          {projectFinalLangs.map((language, index) => (
                            <Grid
                              item
                              key={index}
                              sx={{
                                bgcolor: "lightblue",
                                minWidth: "70px",
                                borderRadius: "var(--radius-md)",
                              }}
                            >
                              <Grid item marginLeft={1}>
                                {getLanguageName(language)}
                              </Grid>
                              <Grid item marginLeft={2}>
                                <IconButton
                                  onClick={() => handleRemoveLanguage(language)}
                                  size="small"
                                  color="error"
                                >
                                  <DeleteIcon />
                                </IconButton>
                              </Grid>
                            </Grid>
                          ))}
                        </Grid>
                      </Grid>
                    </Grid>
                  </Grid>

                  <Grid item xs={12}>
                    <Grid container spacing={3} alignItems="center">
                      <Grid item xs={12} sm={6} md={5}>
                        <UserSelectAutocomplete
                          onUserSelect={(user) => {
                            // Store the complete user object when selected.
                            setSelectedUserObj(user);
                          }}
                          existingUsers={displaySelection}
                          fetchUsersUrl={GET_USERS}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6} md={4}>
                        <FormControl fullWidth>
                          <Select
                            value={selectRole}
                            onChange={(event) => {
                              // Persist the selected role for assignment.
                              setSelectRole(event.target.value);
                            }}
                          >
                            <MenuItem value="Select role" disabled>
                              Select role
                            </MenuItem>
                            <MenuItem value="allRoles">All Roles</MenuItem>
                            <MenuItem value="transcriber">
                              {getRoleOptionLabel("transcriber")}
                            </MenuItem>
                            <MenuItem value="translator">
                              {getRoleOptionLabel("translator")}
                            </MenuItem>
                            <MenuItem value="performancedirection">
                              {getRoleOptionLabel("performancedirection")}
                            </MenuItem>
                            <MenuItem value="lipqualityassurance">
                              {getRoleOptionLabel("lipqualityassurance")}
                            </MenuItem>
                            <MenuItem value="qualityassurance">
                              {getRoleOptionLabel("qualityassurance")}
                            </MenuItem>
                            <MenuItem value="performancedirectionversions">
                              {getRoleOptionLabel("performancedirectionversions")}
                            </MenuItem>
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid item xs={12} md={3}>
                        <Button
                          variant="contained"
                          color="primary"
                          fullWidth
                          onClick={() => {
                            // Ensure a user is selected before assignment.
                            if (!selectedUserObj) {
                              toast.error("Please select an employee.");
                              return;
                            }

                            // Ensure a role is selected before assignment.
                            if (selectRole === "Select role") {
                              toast.error("Please select a role for the user.");
                              return;
                            }

                            // Expand roles when the "all roles" option is chosen.
                            const requestedRoles =
                              selectRole === "allRoles"
                                ? ALL_ROLES
                                : [selectRole];

                            // Check whether the requested role(s) already exist.
                            const alreadySelected = displaySelection.some(
                              (item) =>
                                item.name === selectedUserObj.name &&
                                requestedRoles.every((role) =>
                                  normalizeRoles(item.role).includes(role)
                                )
                            );

                            // Prevent duplicate role assignments.
                            if (alreadySelected) {
                              toast.error(
                                "This employee already has the selected role."
                              );
                              return;
                            }

                            // Build the new employee assignment record.
                            const userItem = {
                              id: selectedUserObj.id,
                              name: selectedUserObj.name,
                              role:
                                selectRole === "allRoles"
                                  ? ALL_ROLES
                                  : selectRole,
                              selectedUserObj,
                            };

                            // Persist the new assignment and reset role selection.
                            setDisplaySelection([
                              ...displaySelection,
                              userItem,
                            ]);
                            setSelectRole("Select role");
                            // setSelectedUserObj(null);
                          }}
                        >
                          Add Employee
                        </Button>
                      </Grid>
                    </Grid>
                  </Grid>

                  <Grid item xs={12}>
                    <Table
                      size="small"
                      sx={{
                        minWidth: 650,
                        bgcolor: "white",
                        "& .MuiTableCell-root": { py: 0.5, px: 1 },
                      }}
                    >
                      <TableHead bgcolor="#5570dc">
                        <TableRow>
                          <TableCell>NAME</TableCell>
                          <TableCell>ROLE</TableCell>
                          <TableCell>ACTIONS</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {displaySelection.map((user, index) => (
                          <TableRow key={index}>
                            <TableCell>{user.name}</TableCell>
                            <TableCell>{formatRoleLabel(user.role)}</TableCell>
                            <TableCell>
                              <Button
                                color="error"
                                onClick={() => handleRemoveEmployees(user.name)}
                              >
                                Remove
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </Grid>

                  <Grid item xs={12}>
                    <Button variant="contained" type="submit">
                      Save
                    </Button>
                  </Grid>
                </Grid>
              </form>
            )}

            {activeTab === "assets" && (
              <Stack spacing={3}>
                <Box sx={{ overflowX: "auto" }}>
                  <Table
                    size="small"
                    sx={{
                      minWidth: 650,
                      bgcolor: "white",
                      "& .MuiTableCell-root": { py: 0.5, px: 1 },
                    }}
                  >
                    <TableHead bgcolor="#5570dc">
                      <TableRow>
                        <TableCell>FILE TYPE</TableCell>
                        <TableCell>URL</TableCell>
                        <TableCell />
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      <TableRow>
                        <TableCell>Original Video</TableCell>
                        <TableCell>
                          {videoFile ? (
                            <a
                              href={videoFile}
                              target="_blank"
                              rel="noopener noreferrer"
                              style={{ color: "#1976d2" }}
                            >
                              {videoFile}
                            </a>
                          ) : (
                            "N/A"
                          )}
                          {describeVideoIngestion(getVideoIngestionState(projectRecord)) && (
                            <Typography variant="body2" color="text.secondary" mt={0.5}>
                              {describeVideoIngestion(getVideoIngestionState(projectRecord))}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                          <ReloadVideoButton
                            projectId={projectId}
                            projectName={projectRecord?.name || projectName}
                            hasVideo={Boolean(videoFile)}
                            blocker={
                              projectRecord
                                ? reloadVideoBlocker(
                                    getProjectTaskStatuses(projectRecord),
                                    projectRecord
                                  )
                                : "Loading the project…"
                            }
                            onStarted={({ runId }) =>
                              setProjectRecord((previous) => ({
                                ...previous,
                                videoIngestion:
                                  pendingVideoIngestion(runId) || previous?.videoIngestion,
                              }))
                            }
                            buttonProps={{ size: "small", variant: "outlined" }}
                          />
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </Box>

                <Stack
                  direction={{ xs: "column", sm: "row" }}
                  spacing={2}
                  justifyContent="space-between"
                  alignItems={{ xs: "flex-start", sm: "center" }}
                >
                  <Box>
                    <Typography variant="h6" fontWeight={600}>
                      Audio files
                    </Typography>
                    <Typography variant="body2" color="text.secondary" mt={0.5}>
                      Preview project audio, open a file in a new tab, or
                      change what an uploaded file contains.
                    </Typography>
                  </Box>
                  {isDirty && (
                    <Button variant="contained" onClick={handleUpdateProject}>
                      Save
                    </Button>
                  )}
                </Stack>

                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: {
                      xs: "minmax(0, 1fr)",
                      md: "repeat(2, minmax(0, 1fr))",
                    },
                    gap: 2,
                    alignItems: "start",
                  }}
                >
                  <AudioAssetCard
                    title="Dialogue Audio"
                    reference={dialogueAudioFile}
                    projectId={projectId}
                  />
                  <AudioAssetCard
                    title="FX Audio"
                    reference={fxFile}
                    projectId={projectId}
                  />
                  {uploadedAudioTypes.map((audio, index) => {
                    const audioType = audio.audioType || "Audio";
                    const title =
                      AUDIO_TYPE_OPTIONS.find(
                        (option) => option.value === audioType
                      )?.label ||
                      audioType.charAt(0).toUpperCase() + audioType.slice(1);
                    const details = [
                      audio.channelType && `${audio.channelType} channel`,
                      audio.uploadType,
                    ].filter(Boolean);

                    return (
                      <AudioAssetCard
                        key={audio.id || `${audioType}-${audio.url || index}`}
                        title={title}
                        reference={audio.url}
                        projectId={projectId}
                        details={details}
                        action={
                          <FormControl size="small" sx={{ minWidth: 150 }}>
                            <Select
                              value={audio.audioType || ""}
                              displayEmpty
                              inputProps={{
                                "aria-label": `Audio type of ${title}`,
                              }}
                              onChange={(e) =>
                                setUploadedAudioTypes((current) =>
                                  current.map((item, itemIndex) =>
                                    itemIndex === index
                                      ? { ...item, audioType: e.target.value }
                                      : item
                                  )
                                )
                              }
                            >
                              <MenuItem value="" disabled>
                                Choose type
                              </MenuItem>
                              {AUDIO_TYPE_OPTIONS.map((option) => (
                                <MenuItem key={option.value} value={option.value}>
                                  {option.label}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        }
                      />
                    );
                  })}
                </Box>

                <ProjectSubtitleFiles
                  projectId={projectId}
                  originalSubtitleObjectName={originalSubtitleObjectName}
                  translatedSubtitles={translatedSubtitles}
                />

                <ProjectSubtitleUpload
                  projectId={projectId}
                  originalSubtitleObjectName={originalSubtitleObjectName}
                  onSubtitlesAttached={(copiedSubtitles) => {
                    copiedSubtitles.forEach(({ role, languageCode, objectName }) => {
                      if (role === "original") {
                        setOriginalSubtitleObjectName(objectName);
                      } else {
                        setTranslatedSubtitles((current) => ({
                          ...current,
                          [languageCode]: objectName,
                        }));
                      }
                    });
                  }}
                />
              </Stack>
            )}
            {activeTab === "actions" && (
              <Box>
                <Grid container spacing={3}>
                  <Grid item xs={12}>
                    {/* Based on the Create/Regenerate translation controls from the project admin actions page. */}
                    <Box sx={actionSectionSx}>
                      <Grid container spacing={2} alignItems="center">
                        <Grid item xs={12}>
                          <Typography variant="h6">
                            Create/Regenerate Translation
                          </Typography>
                        </Grid>
                        <Grid item xs={12} md={4}>
                          <FormControl fullWidth>
                            <InputLabel
                              id="create-translation-language-label"
                              shrink
                            >
                              Language
                            </InputLabel>
                            <Select
                              labelId="create-translation-language-label"
                              label="Language"
                              value={translateLanguage}
                              onChange={(e) =>
                                setTranslateLanguage(e.target.value)
                              }
                              displayEmpty
                            >
                              <MenuItem value="">
                                <em>Select a language</em>
                              </MenuItem>
                              {availableDefaultLanguages.length === 0 && (
                                <MenuItem value="" disabled>
                                  No languages added
                                </MenuItem>
                              )}
                              {availableDefaultLanguages.map((languageCode) => (
                                <MenuItem
                                  key={languageCode}
                                  value={languageCode}
                                >
                                  {getLanguageName(languageCode)}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </Grid>
                        <Grid item xs={12} md={5}>
                          <ModeCards
                            value={translateMode}
                            onChange={setTranslateMode}
                            options={RETRANSLATE_MODE_OPTIONS}
                            ariaLabel="Translation scope"
                          />
                        </Grid>
                        <Grid item xs={12} md={3}>
                          <Button
                            variant="contained"
                            color="primary"
                            fullWidth
                            disabled={!translateLanguage}
                            onClick={handleCreateTranslation}
                            sx={actionButtonSx}
                          >
                            Create Translation
                          </Button>
                        </Grid>
                      </Grid>
                    </Box>
                  </Grid>
                  <Grid item xs={12}>
                    <Box sx={actionSectionSx}>
                      <Grid container spacing={2} alignItems="center">
                        <Grid item xs={12}>
                          <Typography variant="h6">Import Cast</Typography>
                          <Typography variant="body2" color="text.secondary">
                            Copy characters from another project into this one.
                            Existing characters are kept and duplicates are skipped.
                          </Typography>
                        </Grid>
                        <Grid item xs={12}>
                          <ImportCharactersPanel projectId={projectId} />
                        </Grid>
                      </Grid>
                    </Box>
                  </Grid>
                  <Grid item xs={12}>
                    <Box sx={actionSectionSx}>
                      <Grid container spacing={2} alignItems="center">
                        <Grid item xs={12}>
                          <Typography variant="h6">
                            Set Default Voice Model
                          </Typography>
                        </Grid>
                        <Grid item xs={12} md={5}>
                          <FormControl fullWidth>
                            <InputLabel id="default-voice-model-label" shrink>
                              Default Voice Model
                            </InputLabel>
                            <Select
                              labelId="default-voice-model-label"
                              label="Default Voice Model"
                              value={defaultVoiceModel}
                              onChange={(e) =>
                                setDefaultVoiceModel(e.target.value)
                              }
                              displayEmpty
                            >
                              <MenuItem value="">
                                <em>Select a model</em>
                              </MenuItem>
                              {VOICE_ENGINE_OPTIONS.map((option) => (
                                <MenuItem
                                  key={option.value}
                                  value={option.value}
                                >
                                  {option.label}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </Grid>
                        <Grid item xs={12} md={4}>
                          <FormControl fullWidth>
                            <InputLabel
                              id="default-voice-language-label"
                              shrink
                            >
                              Language
                            </InputLabel>
                            <Select
                              labelId="default-voice-language-label"
                              label="Language"
                              value={defaultVoiceLanguage}
                              onChange={(e) =>
                                setDefaultVoiceLanguage(e.target.value)
                              }
                              displayEmpty
                            >
                              <MenuItem value="">
                                <em>Select a language</em>
                              </MenuItem>
                              {availableDefaultLanguages.length === 0 && (
                                <MenuItem value="" disabled>
                                  No languages added
                                </MenuItem>
                              )}
                              {availableDefaultLanguages.map((languageCode) => (
                                <MenuItem
                                  key={languageCode}
                                  value={languageCode}
                                >
                                  {getLanguageName(languageCode)}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </Grid>
                        <Grid item xs={12} md={3}>
                          <Button
                            variant="contained"
                            color="primary"
                            fullWidth
                            disabled={isSettingDefaultVoiceModel}
                            onClick={handleSetDefaultVoiceModel}
                            sx={actionButtonSx}
                          >
                            Set Default
                          </Button>
                        </Grid>
                      </Grid>
                    </Box>
                  </Grid>
                  <Grid item xs={12}>
                    {/* Based on the Create/Regenerate takes controls from the project admin actions page. */}
                    <Box sx={actionSectionSx}>
                      <Grid container spacing={2} alignItems="center">
                        <Grid item xs={12}>
                          <Typography variant="h6">
                            Create/Regenerate Takes
                          </Typography>
                        </Grid>
                        <Grid item xs={12} md={4}>
                          <FormControl fullWidth>
                            <InputLabel id="create-takes-language-label" shrink>
                              Language
                            </InputLabel>
                            <Select
                              labelId="create-takes-language-label"
                              label="Language"
                              value={createTakesLanguage}
                              onChange={(e) =>
                                setCreateTakesLanguage(e.target.value)
                              }
                              displayEmpty
                            >
                              <MenuItem value="">
                                <em>Select a language</em>
                              </MenuItem>
                              {availableDefaultLanguages.length === 0 && (
                                <MenuItem value="" disabled>
                                  No languages added
                                </MenuItem>
                              )}
                              {availableDefaultLanguages.map((languageCode) => (
                                <MenuItem
                                  key={languageCode}
                                  value={languageCode}
                                >
                                  {getLanguageName(languageCode)}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </Grid>
                        <Grid item xs={12} md={5}>
                          <ModeCards
                            value={createTakesMode}
                            onChange={setCreateTakesMode}
                            options={CREATE_TAKES_MODE_OPTIONS}
                            ariaLabel="Create takes scope"
                          />
                        </Grid>
                        <Grid item xs={12} md={3}>
                          <Button
                            variant="contained"
                            color="primary"
                            fullWidth
                            disabled={!createTakesLanguage}
                            onClick={handleCreateTakes}
                            sx={actionButtonSx}
                          >
                            Create Takes
                          </Button>
                        </Grid>
                      </Grid>
                    </Box>
                  </Grid>
                </Grid>
              </Box>
            )}
          </Box>
        </Box>
      </Box>
    </Container>
  );
};

export default ProjectEditScreen;
